import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import Layout from '@/components/Layout';
import StickyBar from '@/components/StickyBar';
import { RiskBadge, RiskAttestationMarker } from '@/components/RiskScoreCard';
import { Button } from '@/components/ui/button';
import {
  Plus,
  FileText,
  Clock,
  ChevronRight,
  ChevronLeft,
  Loader2,
  Trash2,
  AlertCircle,
  CheckCircle,
  PauseCircle,
  User
} from 'lucide-react';
import api from '@/lib/api';
import { toastSuccess, toastError } from '@/lib/toast';
import { formatDate } from '@/lib/utils';
import { getStatusLabel } from '@/lib/statusLabels';
import {
  NO_AMOUNT_TITLE,
  formatDenominator,
  formatEntryAmount,
  formatUsdTotal,
} from '@/lib/procurementAmount';

// Constants
const MAX_REQUIREMENT_PREVIEW = 100;

// THE PAGE SIZES the server offers. It REFUSES anything else rather than
// clamping, so this list must stay in step with PAGE_SIZES in
// backend/routes/_procurement_status_groups.py. Kept as a plain literal
// rather than fetched, because a select that cannot render until a request
// returns is worse than a three-item list stated twice.
const PAGE_SIZES = [25, 50, 100];

// The GROUP KEYS are the server's. The browser never decides which
// statuses belong to which tile - it sends this key and the server maps it
// - so no status grouping is mirrored here and there is no twin to drift.
const TILE_ORDER = ['drafting', 'in_progress', 'completed'];
const TILE_META = {
  drafting: { label: 'Drafting', icon: FileText, testId: 'stat-drafting',
    shell: 'from-slate-800 to-slate-900 border-slate-700/50', muted: 'text-slate-400' },
  in_progress: { label: 'In progress', icon: Clock, testId: 'stat-active',
    shell: 'from-blue-600 to-blue-700 border-blue-500/30', muted: 'text-blue-200' },
  completed: { label: 'Completed', icon: CheckCircle, testId: 'stat-completed',
    shell: 'from-emerald-600 to-emerald-700 border-emerald-500/30', muted: 'text-emerald-200' },
};

// THE STALE-RATE CAVEAT, SHOWN RATHER THAN ASSUMED (Ashok ruling,
// 2026-09-01). resolve_rate takes the newest exchange rate at or before
// the entry date with NO staleness bound, so a summed tile is NOT a
// valuation as of today - it is a sum of figures each frozen when its
// budget was answered. Saying so is the difference between a number a
// reader can use and one they will quietly misread.
//
// WORDING AND SIZE ARE ASHOK'S, 2026-09-01. Was "USD totals are converted
// at time of entry, not revalued at today's rates." at text-[10px]; now
// this sentence at text-xs. The caveat was set smaller than every other
// line on the bar, which is the wrong emphasis for the one line that says
// what the numbers above it do NOT mean.
const RATE_FOOTNOTE = 'Amounts in Dashboard are in USD converted at time of transaction and not revalued at today’s rates';

// THE FILTER HINT. The tiles are buttons and nothing about them says so -
// a buyer will not discover it unaided.
//
// IT SWAPS WHEN A FILTER IS ON, and that is the whole point: "click a
// status to filter" is not merely redundant once one is selected, it is
// wrong - it describes an action the reader has already taken and says
// nothing about how to undo it. The active form names the group in force
// and points at BOTH ways out, because both exist: re-clicking the tile,
// and the explicit Clear filter control beside the list heading.
const FILTER_HINT_IDLE = 'Tip: click a status tile to filter the list below.';
const filterHintActive = (label) =>
  `Showing ${label} only — click the tile again, or Clear filter, to show all.`;

// Sweep C-phase Item 1 (2026-07-21): the label text itself now comes from
// the single shared vocabulary (lib/statusLabels.js) - only the color
// mapping (a visual affordance, not a vocabulary) stays local here.
const STATUS_COLORS = {
  'completed': 'bg-green-100 text-green-700',
  'rfp_generated': 'bg-blue-100 text-blue-700',
  'default': 'bg-zinc-100 text-zinc-600'
};

const getStatusColor = (status) => STATUS_COLORS[status] || STATUS_COLORS.default;

// Loading state component
const LoadingState = () => (
  <div className="flex items-center justify-center py-16">
    <Loader2 className="w-6 h-6 animate-spin text-zinc-400" />
  </div>
);

// Error state component
const ErrorState = ({ message }) => (
  <div className="flex flex-col items-center justify-center py-16 text-center">
    <AlertCircle className="w-8 h-8 text-red-500 mb-3" />
    <p className="text-zinc-600">{message}</p>
  </div>
);

// Empty state component
const EmptyState = ({ filtered }) => (
  <div className="flex flex-col items-center justify-center py-16 text-center">
    <FileText className="w-10 h-10 text-zinc-300 mb-3" strokeWidth={1.5} />
    <p className="text-zinc-500 mb-4" data-testid="dashboard-empty">
      {filtered ? 'No procurements in this group' : 'No procurements yet'}
    </p>
    {!filtered && (
      <Link to="/new">
        <Button className="bg-[#0055FF] hover:bg-[#0044CC] text-white">
          Start Your First Procurement
        </Button>
      </Link>
    )}
  </div>
);

// One status tile. Clickable: it is the filter control for its group.
const StatTile = ({ group, stat, active, loading, onToggle }) => {
  const meta = TILE_META[group];
  const Icon = meta.icon;
  const canCarryAmount = stat?.can_carry_amount !== false;
  const total = canCarryAmount ? formatUsdTotal(stat?.sum_usd) : null;
  return (
    <button
      type="button"
      onClick={() => onToggle(group)}
      aria-pressed={active}
      data-testid={meta.testId}
      data-active={active ? 'true' : 'false'}
      className={`text-left bg-gradient-to-br ${meta.shell} rounded-lg py-2 px-3 shadow-sm border relative overflow-hidden transition-all ${
        active ? 'ring-2 ring-white/70' : 'hover:brightness-110'
      }`}
    >
      <div className="absolute top-0 right-0 w-20 h-20 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2"></div>
      <div className="relative flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <div className="p-1.5 bg-white/10 rounded-md shrink-0">
            <Icon className={`w-3.5 h-3.5 ${meta.muted}`} />
          </div>
          <span className={`text-[10px] font-semibold uppercase tracking-wider ${meta.muted} truncate`}>
            {meta.label}
          </span>
        </div>
        <div className="text-2xl font-bold text-white shrink-0" data-testid={`${meta.testId}-count`}>
          {loading ? '-' : (stat?.count ?? 0)}
        </div>
      </div>
      <div className="relative mt-1 min-h-[1.75rem]">
        {loading ? null : canCarryAmount ? (
          <>
            {/* THE TILE SUMS USD and says so, because the row beside it
                shows the buyer's own currency. Two different numbers for
                one document read as an error unless each names its unit. */}
            <div className="text-sm font-semibold text-white/90" data-testid={`${meta.testId}-amount`}>
              {total ?? '$0'} <span className="text-[10px] font-normal text-white/60">USD</span>
            </div>
            {/* THE DENOMINATOR. 186 of 244 documents carry no amount, so a
                bare sum understates silently. */}
            <div className={`text-[10px] ${meta.muted}`} data-testid={`${meta.testId}-denominator`}>
              {formatDenominator(stat?.with_amount, stat?.count)}
            </div>
          </>
        ) : (
          // THE GROUP THAT CANNOT CARRY AN AMOUNT. These documents sit
          // before submit-answers, where the budget is captured, so "$0"
          // here would state a budget of zero rather than "not asked yet".
          <div className={`text-[11px] ${meta.muted}`} data-testid={`${meta.testId}-no-amount`}>
            {stat?.no_amount_reason || 'no budget captured yet'}
          </div>
        )}
      </div>
    </button>
  );
};

// Procurement item component
const ProcurementItem = ({ procurement, onDelete }) => {
  const entryAmount = formatEntryAmount(procurement);
  return (
    <Link
      key={procurement.id}
      to={`/procurement/${procurement.id}`}
      className="flex items-center justify-between px-6 py-4 hover:bg-zinc-50/50 transition-colors group"
      data-testid={`procurement-item-${procurement.id}`}
    >
      <div className="flex items-start gap-4 flex-1 min-w-0">
        <div className="p-2 bg-zinc-100 rounded-sm">
          <FileText className="w-5 h-5 text-zinc-600" strokeWidth={1.5} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 mb-1">
            {procurement.serial_id && (
              <span className="text-[#0055FF] font-mono text-sm font-semibold" data-testid="procurement-serial-id">
                {procurement.serial_id}
              </span>
            )}
            <span className={`px-2 py-0.5 text-xs font-medium ${getStatusColor(procurement.status)}`}>
              {getStatusLabel(procurement.status)}
            </span>
            {/* PARKED IS A BADGE, NOT A TILE (Ashok ruling, 2026-09-01) -
                it cuts across every status, so a tile would double-count a
                document that is both parked and in progress. */}
            {procurement.is_parked && (
              <span
                className="flex items-center gap-1 px-2 py-0.5 text-xs font-medium bg-amber-100 text-amber-800"
                data-testid={`procurement-parked-${procurement.id}`}
                title="Awaiting a master-data update"
              >
                <PauseCircle className="w-3 h-3" />
                Parked
              </span>
            )}
          </div>
          <p className="text-zinc-900 font-medium truncate">
            {procurement.user_requirement?.slice(0, MAX_REQUIREMENT_PREVIEW)}
            {procurement.user_requirement?.length > MAX_REQUIREMENT_PREVIEW ? '...' : ''}
          </p>
          <div className="flex items-center gap-4 mt-1">
            <span className="flex items-center gap-1 text-sm text-zinc-500">
              <Clock className="w-3 h-3" />
              {formatDate(procurement.created_at)}
            </span>
            {/* THE AMOUNT CHIP, beside the creation date, in the BUYER'S
                OWN currency - distinct background so it is not mistaken
                for the tile's USD total. */}
            {entryAmount ? (
              <span
                className="px-2 py-0.5 text-xs font-medium bg-zinc-100 text-zinc-700 rounded-sm"
                data-testid={`procurement-amount-${procurement.id}`}
              >
                {entryAmount}
              </span>
            ) : (
              // A MUTED DASH, NEVER A ZERO. No budget has been captured -
              // that is an absence, and "0" would read as a stated budget.
              <span
                className="text-sm text-zinc-300"
                data-testid={`procurement-no-amount-${procurement.id}`}
                title={NO_AMOUNT_TITLE}
                aria-label={NO_AMOUNT_TITLE}
              >
                &mdash;
              </span>
            )}
            {/* Item 3a (2026-07-21): owner identity on each dashboard card. */}
            {procurement.owner_name && (
              <span
                className="flex items-center gap-1 text-sm text-zinc-500"
                data-testid="procurement-owner"
                title={procurement.owner_email || undefined}
              >
                <User className="w-3 h-3" />
                {procurement.owner_name}
              </span>
            )}
            {procurement.risk_assessment && (
              <RiskBadge level={procurement.risk_assessment.risk_level} />
            )}
            {/* Staleness gate: the stamped as-issued marker, compact. */}
            <RiskAttestationMarker riskAssessment={procurement.risk_assessment} compact />

          </div>
        </div>
      </div>
      <div className="flex items-center gap-2">
        {/* DELETE FREEZE GUARD (2026-07-27, Task 7 merge-review item 1):
            a completed procurement can no longer be deleted (backend 409)
            - hide the affordance here rather than let the click round-trip
            to a rejected request. */}
        {procurement.status !== 'completed' && (
          <button
            onClick={(e) => onDelete(procurement.id, e)}
            className="p-2 text-zinc-400 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-all"
            data-testid={`delete-procurement-${procurement.id}`}
          >
            <Trash2 className="w-4 h-4" />
          </button>
        )}
        <ChevronRight className="w-5 h-5 text-zinc-400 group-hover:text-zinc-600 transition-colors" />
      </div>
    </Link>
  );
};

// Procurement list content component
const ProcurementListContent = ({ loading, error, procurements, onDelete, filtered }) => {
  if (loading) return <LoadingState />;
  if (error) return <ErrorState message={error} />;
  if (procurements.length === 0) return <EmptyState filtered={filtered} />;

  return (
    <div className="divide-y divide-zinc-100">
      {procurements.map((procurement) => (
        <ProcurementItem
          key={procurement.id}
          procurement={procurement}
          onDelete={onDelete}
        />
      ))}
    </div>
  );
};

const Dashboard = () => {
  const [procurements, setProcurements] = useState([]);
  const [stats, setStats] = useState(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZES[0]);
  const [group, setGroup] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statsLoading, setStatsLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchProcurements = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.getAllProcurements({ page, pageSize, group });
      setProcurements(response.data?.items || []);
      setTotal(response.data?.total || 0);
    } catch (err) {
      console.error('Failed to load procurements:', err);
      setError('Failed to load procurements');
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, group]);

  // THE TILES ARE FETCHED SEPARATELY AND ARE NEVER FILTERED. They describe
  // the whole estate, so clicking one must not change what the others say -
  // otherwise the tile you clicked would be the only one left standing.
  const fetchStats = useCallback(async () => {
    try {
      setStatsLoading(true);
      const response = await api.getProcurementStats();
      setStats(response.data || null);
    } catch (err) {
      console.error('Failed to load procurement stats:', err);
      setStats(null);
    } finally {
      setStatsLoading(false);
    }
  }, []);

  useEffect(() => { fetchProcurements(); }, [fetchProcurements]);
  useEffect(() => { fetchStats(); }, [fetchStats]);

  // FILTER CLEARING: the active tile is a TOGGLE. Clicking the active tile
  // clears the filter, and a "Clear filter" control appears beside the list
  // heading as well - a filter with no visible way out is how a user
  // concludes their data is gone.
  const toggleGroup = (next) => {
    setGroup((current) => (current === next ? null : next));
    setPage(1);
  };

  const changePageSize = (size) => {
    setPageSize(size);
    setPage(1);
  };

  const handleDelete = async (id, e) => {
    e.preventDefault();
    e.stopPropagation();

    if (!window.confirm('Are you sure you want to delete this procurement?')) return;

    try {
      await api.deleteProcurement(id);
      toastSuccess('Procurement deleted');
      fetchProcurements();
      fetchStats();
    } catch (err) {
      // DELETE FREEZE GUARD (2026-07-27): surface the backend's 409
      // message ("This procurement is completed and can no longer be
      // deleted.") verbatim rather than a generic fallback.
      const detail = err?.response?.data?.detail;
      toastError(typeof detail === 'string' ? detail : 'Failed to delete procurement');
    }
  };

  const statByGroup = {};
  (stats?.groups || []).forEach((g) => { statByGroup[g.group] = g; });
  const lastPage = Math.max(1, Math.ceil(total / pageSize));
  const firstRow = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const lastRow = Math.min(page * pageSize, total);

  return (
    <Layout>
      {/* Pinned zone: heading + New Procurement button + stats, flush under the
          logo bar (direct child of Layout, no padded wrapper above it). */}
      <StickyBar className="px-6 md:px-12 py-2">
        <div className="flex items-center justify-between gap-4 mb-2">
          <h1 className="text-lg font-semibold text-zinc-900">Procurement Dashboard</h1>
          <Link to="/new">
            <Button
              size="sm"
              className="bg-[#0055FF] hover:bg-[#0044CC] text-white"
              data-testid="new-procurement-btn"
            >
              <Plus className="w-4 h-4 mr-1" strokeWidth={1.5} />
              New Procurement
            </Button>
          </Link>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {TILE_ORDER.map((key) => (
            <StatTile
              key={key}
              group={key}
              stat={statByGroup[key]}
              active={group === key}
              loading={statsLoading}
              onToggle={toggleGroup}
            />
          ))}
        </div>

        <div className="flex items-center justify-between gap-3 mt-1.5 flex-wrap">
          <div className="flex items-center gap-3 flex-wrap min-w-0">
            <p className="text-xs text-zinc-500" data-testid="rate-footnote">{RATE_FOOTNOTE}</p>
            <p className="text-xs text-zinc-400" data-testid="filter-hint">
              {group ? filterHintActive(TILE_META[group].label) : FILTER_HINT_IDLE}
            </p>
          </div>
          {/* PARKED: ONE COUNT ABOVE THE LIST, not a tile. */}
          {!statsLoading && (stats?.parked || 0) > 0 && (
            <span
              className="flex items-center gap-1 text-[11px] font-medium text-amber-700"
              data-testid="parked-count"
            >
              <PauseCircle className="w-3 h-3" />
              {stats.parked} parked, awaiting a master-data update
            </span>
          )}
        </div>
      </StickyBar>

      <div className="px-6 pb-6 md:px-12 md:pb-12 pt-6 animate-fade-in">
        {/* Procurement List */}
        <div className="bg-white border border-zinc-200">
          <div className="px-6 py-4 border-b border-zinc-200 flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-3">
              <h2 className="text-lg font-semibold text-zinc-900">
                {group ? TILE_META[group].label : 'Recent Procurements'}
              </h2>
              {group && (
                <button
                  type="button"
                  onClick={() => toggleGroup(group)}
                  className="text-xs text-[#0055FF] hover:underline"
                  data-testid="clear-filter"
                >
                  Clear filter
                </button>
              )}
            </div>
            <div className="flex items-center gap-3">
              <span className="text-xs text-zinc-500" data-testid="page-range">
                {total === 0 ? '0 of 0' : `${firstRow}–${lastRow} of ${total}`}
              </span>
              <label className="text-xs text-zinc-500 flex items-center gap-1">
                Rows
                <select
                  value={pageSize}
                  onChange={(e) => changePageSize(Number(e.target.value))}
                  className="border border-zinc-200 rounded-sm px-1 py-0.5 text-xs"
                  data-testid="page-size"
                  aria-label="Rows per page"
                >
                  {PAGE_SIZES.map((size) => (
                    <option key={size} value={size}>{size}</option>
                  ))}
                </select>
              </label>
            </div>
          </div>

          <ProcurementListContent
            loading={loading}
            error={error}
            procurements={procurements}
            onDelete={handleDelete}
            filtered={Boolean(group)}
          />

          {total > pageSize && (
            <div className="px-6 py-3 border-t border-zinc-200 flex items-center justify-between">
              <Button
                size="sm"
                variant="outline"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                data-testid="page-prev"
              >
                <ChevronLeft className="w-4 h-4 mr-1" /> Previous
              </Button>
              <span className="text-xs text-zinc-500" data-testid="page-indicator">
                Page {page} of {lastPage}
              </span>
              <Button
                size="sm"
                variant="outline"
                disabled={page >= lastPage}
                onClick={() => setPage((p) => Math.min(lastPage, p + 1))}
                data-testid="page-next"
              >
                Next <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default Dashboard;
